import { Grid, Typography } from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import { Link, useLocation, useParams } from 'react-router-dom';

import { Embed, models } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';

import Breadcrumbs from 'common/components/Breadcrumbs/Breadcrumbs';
import { Loader } from 'common/components/Loader/Loader';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { REPORTING_LIBRARY_MAIN_HEADING } from 'common/constants/ReportingLibraryConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import {
	APP_GENERAL_ERROR_MSG,
	APP_GENERIC_BAD_REQUEST_MSG,
	APP_GENERIC_DATA_NOT_FOUND_MSG,
} from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { getQueryParams, urlWithTenant } from 'common/utils/utilFunctions';

import { getReportDetail } from './api';
import { getIframeHeight } from './ReportingLibraryUtils';

import reportingStyles from './ReportingLibraryDetail.module.scss';

const { OK, BAD_REQUEST, NOT_FOUND } = HTTP_STATUS_CODES;

declare global {
	interface Window {
		report: Embed;
	}
}

const ReportingLibraryDetail = () => {
	const params = useParams();

	const location = useLocation();

	const { reportId = '' } = params;

	const { reportName = '' } = location.state;

	const isAppDrawerOpen = useSelector(
		(state: RootState) => state.common.isAppDrawerOpen,
	);

	const [isToastOpen, setIsToastOpen] = useState<boolean>(false);
	const [toastMessage, setToastMessage] = useState('');
	const [embededToken, setEmbededToken] = useState<string | undefined>(
		undefined,
	);
	const [embedUrl, setEmbedUrl] = useState<string | undefined>(undefined);
	const [embededReportId, setEmbededReportId] = useState<string>('');

	const handleError = (statusCode: number) => {
		switch (statusCode) {
			case BAD_REQUEST:
				setToastMessage(APP_GENERIC_BAD_REQUEST_MSG);
				break;
			case NOT_FOUND:
				setToastMessage(APP_GENERIC_DATA_NOT_FOUND_MSG);
				break;
			default:
				setToastMessage(APP_GENERAL_ERROR_MSG);
				break;
		}
	};
	const fetchReportDetail = async () => {
		try {
			const response = await getReportDetail(reportId);
			if (response?.statusCode === OK) {
				const { responseBody } = response;
				const url = responseBody?.embedReports[0].embedUrl;
				const searchParams = getQueryParams(url);
				setEmbededToken(responseBody?.embedToken.token);
				setEmbededReportId(searchParams.reportId ?? '');
				setEmbedUrl(url);
			} else {
				setIsToastOpen(true);
				handleError(response?.statusCode);
			}
		} catch (ex) {
			setIsToastOpen(true);
			setToastMessage(APP_GENERAL_ERROR_MSG);
		}
	};

	const handleToast = useCallback(() => {
		setIsToastOpen(false);
		setToastMessage('');
	}, []);

	const breadCrumbs = [
		<Link
			key="reporting-library"
			to={urlWithTenant(`/${rrcpDashboardRoutes.reporting}`)}
			className={reportingStyles.link}
		>
			{REPORTING_LIBRARY_MAIN_HEADING}
		</Link>,
		<Typography key="report-details" data-testid="report-name">
			{reportName}
		</Typography>,
	];

	useEffect(() => {
		fetchReportDetail();
	}, [reportId]);

	useEffect(() => {
		const adjustIframeHeight = () => {
			const iframe = document.querySelector('iframe') as HTMLIFrameElement;
			if (iframe) {
				const screenWidth = window.screen.width;
				const screenHeight = window.screen.height;
				iframe.style.height = getIframeHeight(
					screenWidth,
					screenHeight,
					isAppDrawerOpen,
				);
			}
		};

		adjustIframeHeight();

		window.addEventListener('resize', adjustIframeHeight);

		return () => {
			window.removeEventListener('resize', adjustIframeHeight);
		};
	}, [embededToken, embedUrl, isAppDrawerOpen]);

	return (
		<Grid container>
			<Grid item xs={12}>
				<Breadcrumbs
					breadcrumbs={breadCrumbs}
					className={reportingStyles['reporting-bread-crumbs']}
					customClass
				/>
				{embededToken && embedUrl ? (
					<div data-testid="power-bi-report">
						<PowerBIEmbed
							embedConfig={{
								type: 'report', // Supported types: report, dashboard, tile, visual, qna, paginated report and create
								id: embededReportId,
								embedUrl,
								accessToken: embededToken,
								tokenType: models.TokenType.Embed, // Use models.TokenType.Aad for SaaS embed
								settings: {
									layoutType: models.LayoutType.Custom,
									customLayout: {
										displayOption: models.DisplayOption.FitToWidth,
									},
									panes: {
										filters: {
											expanded: false,
											visible: false,
										},
									},
									navContentPaneEnabled: false,
									background: models.BackgroundType.Transparent,
								},
							}}
							cssClassName={
								isAppDrawerOpen
									? reportingStyles['embed-container-menu-open']
									: reportingStyles['embed-container-menu-close']
							}
							getEmbeddedComponent={(embeddedReport) => {
								window.report = embeddedReport;
							}}
						/>
					</div>
				) : (
					<div
						className={reportingStyles['reporting-loader']}
						data-testid="loader"
					>
						<Loader show />
					</div>
				)}
			</Grid>
			<AlertComponent
				open={isToastOpen}
				toastMessage={toastMessage}
				severity="error"
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</Grid>
	);
};

export default ReportingLibraryDetail;
